import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Image from "../components/common/Image";

import { useEffect, useState } from "react";
import useScreen from "../components/common/UseScreen";
import ProductForm, { FormData } from "../components/products-and-services/ProductForm";
import { ProductOrService, ProductOrServiceId, productsAndServices } from "../data/products-and-services";
import { SnackbarMsg, SupportText } from "../data/texts";
import { Icons, Images } from "../models/assets-files";
import { BankDisputeService, CorrespondenceService, TrackMasterService, WebIntelligenceService } from "../services/product.service";
import { ImageService } from "../services/image.service";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../store/system.store";
import { auth } from "../config/firebase";

const ProductPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [clientData, setClientData] = useState<any>(null);
  const location = useLocation();
  const { productId } = useParams();
  const theme = useTheme();

  const navigate = useNavigate();
  const [product, setProduct] = useState<ProductOrService>();
  const dispatch = useDispatch();

  const { isMobile } = useScreen();
  const wrapBreakingPoint = useMediaQuery(theme.breakpoints.up(1200));

  useEffect(() => {
    initProduct();
  }, [productId]);

  useEffect(() => {
    // Check if we have client data from agent navigation
    if (location.state?.clientData) {
      setClientData(location.state.clientData);
    }
  }, [location.state]);

  const handleFormSubmit = async (formData: FormData, href: string) => {
    try {
      setIsLoading(true);
      console.log("clientData ", clientData);
      // For agent that are fills the form for a client
      if (clientData) {
        formData = {
          ...formData,
          agentClientInfo: JSON.stringify({
            crmProfileId: clientData.crmProfileId,
            clientId: clientData.id,
            clientName: clientData.name,
            clientEmail: clientData.email,
            totalAmount: clientData.totalAmount,
          }),
        };
      }

      if (product) {
        console.log("formData ", formData);
        switch (product.id) {
          case ProductOrServiceId.CORRESPONDENCE:
            formData = {
              ...formData,
              type: formData?.type.camelCaseToKebabCase(),
            };
            await CorrespondenceService.sendCorrespondenceRequest(formData, href, product.hasFile);
            break;
          // Blockchain analysis is now handled by a dedicated page (BlockchainRequestPage.tsx)
          // case ProductOrServiceId.BLOCKCHAIN_ANALYSIS:
          //   await BlockchainAnalysisService.sendBlockchainAnalysisRequest(
          //     formData,
          //     href,
          //     product.hasFile
          //   );
          //   break;
          case ProductOrServiceId.TRACK_MASTER:
            await TrackMasterService.sendTrackMasterRequest(formData, href, product.hasFile);
            break;
          case ProductOrServiceId.WEB_INTELLIGENCE:
            await WebIntelligenceService.sendWebIntelligenceRequest(formData, href, product.hasFile);
            break;
          case ProductOrServiceId.BANK_DISPUTE:
            await BankDisputeService.sendBankDisputeRequest(formData, href, product.hasFile);
            break;
          case ProductOrServiceId.EXCHANGE_DISPUTE:
            await BankDisputeService.sendBankDisputeRequest(formData, href, product.hasFile);
            break;
        }

        dispatch(showSnackbar({ msg: SnackbarMsg.FORM_SUBMIT }));
        setIsLoading(false);
        // Force token refresh before navigation
        if (auth.currentUser) {
          await auth.currentUser.getIdToken(true);
        }
        // Add a small delay to ensure token is refreshed
        setTimeout(() => {
          navigate("/");
        }, 500);
      }
    } catch (error: any) {
      setIsLoading(false);
      console.error(error);

      dispatch(
        showSnackbar({
          msg: error?.response?.data?.message || SnackbarMsg.SOMETHING_WRONG,
          isError: true,
          disableAutoHide: true,
        })
      );
    }
  };

  const initProduct = () => {
    const product = productsAndServices.find((product) => product.id === productId);

    if (!product) {
      navigate("/");
      return;
    }
    setProduct(product);
  };

  if (!product) return <></>;
  return (
    <Box
      sx={{
        ...(isLoading ? theme.customStyles.disabled : ""),
        display: "flex",
        flexWrap: wrapBreakingPoint ? "nowrap" : "wrap",
        gap: "50px",
        background: "white",
        padding: "24px",
        justifyContent: "space-between",
      }}
    >
      {isLoading && <CircularProgress sx={{ position: "absolute", top: "50%", left: "50%" }} />}
      <Box
        sx={{
          paddingInlineStart: isMobile ? "0" : "20px",
          ...theme.customStyles.columnStart,
          width: wrapBreakingPoint ? "50%" : "100%",
        }}
      >
        <Box sx={{ height: "200px" }}>
          <Image
            sx={{ alignSelf: "center", height: "188px" }}
            src={ImageService.getLocalImgPath(Images.Folders.PRODUCT_PAGE, Images.ProductPage.MAGNIFY_GLASS_WOMAN)}
          />
        </Box>
        <Box
          sx={{
            ...theme.customStyles.flexStart,
            gap: "12px",
            marginBottom: "12px",
          }}
        >
          <Image sx={{ width: "40px", height: "40px" }} src={ImageService.getLocalIconPath(Icons.Folders.PRODUCTS_AND_SERVICES, product.icon)} />
          <Typography
            sx={{
              fontWeight: "400",
              fontSize: "30px",
              color: theme.palette.primary.main,
            }}
          >
            {product.title}
          </Typography>
        </Box>
        <Typography
          sx={{
            color: theme.palette.secondary.main,
            fontWeight: "300",
            whiteSpace: "pre-wrap",
            marginBottom: isMobile ? "10px" : "50px",
          }}
        >
          {product.description}
        </Typography>
        <Box
          sx={{
            ...theme.customStyles.flexStart,
            gap: "8px",
            alignSelf: "flex-start",
          }}
        >
          <Typography
            sx={{
              fontWeight: "300",
              color: theme.palette.secondary.main,
            }}
          >
            {SupportText.HAVE_QUESTIONS}
          </Typography>
          <Typography
            sx={{
              ...theme.customStyles.link,
            }}
          >
            {SupportText.WERE_HERE_TO_HELP}
          </Typography>
        </Box>
      </Box>
      <ProductForm
        onSubmit={handleFormSubmit}
        containerSx={{
          width: wrapBreakingPoint ? "45%" : "100%",
        }}
        product={product}
      />
    </Box>
  );
};

export default ProductPage;
